import useAuth from '@/features/auth/hooks/use-auth'
import {
  NegotiationsPermission,
  StateTransitionPermission
} from '@/features/roles/consts/permissions'
import { type IStatePermission, TypeAccount } from '@/features/users/models/user'

export const useStatePermissions = () => {
  const { auth } = useAuth()

  const isAdmin = (): boolean => {
    return auth?.typeAccount === TypeAccount.admin
  }

  const hasStatePermission = (stateId: string, permission: StateTransitionPermission): boolean => {
    // Los administradores tienen acceso completo a todos los estados
    if (isAdmin()) {
      return true
    }

    if (!auth?.statePermissions || auth.statePermissions.length === 0) {
      return false
    }

    const statePermission = auth.statePermissions.find(
      (sp: IStatePermission) => sp.stateId === stateId
    )

    return statePermission ? statePermission.permissions.includes(permission) : false
  }

  const canReadState = (stateId: string): boolean => {
    return hasStatePermission(stateId, StateTransitionPermission.ReadStateTransition)
  }

  const canExecuteState = (stateId: string): boolean => {
    return hasStatePermission(stateId, StateTransitionPermission.ExecuteStateTransition)
  }

  // Editar se evalúa contra el estado ACTUAL de la negociación,
  // a diferencia de canExecuteState que razona sobre el estado destino
  const canEditState = (stateId: string): boolean => {
    return hasStatePermission(stateId, StateTransitionPermission.EditStateTransition)
  }

  const getReadableStates = (): string[] => {
    // Los administradores pueden leer todos los estados
    if (isAdmin()) {
      return [] // Retorna array vacío indicando "sin restricciones"
    }

    if (!auth?.statePermissions) return []

    return auth.statePermissions
      .filter((sp) => sp.permissions.includes(StateTransitionPermission.ReadStateTransition))
      .map((sp) => sp.stateId)
  }

  const getExecutableStates = (): string[] => {
    // Los administradores pueden ejecutar en todos los estados
    if (isAdmin()) {
      return [] // Retorna array vacío indicando "sin restricciones"
    }

    if (!auth?.statePermissions) return []

    return auth.statePermissions
      .filter((sp) => sp.permissions.includes(StateTransitionPermission.ExecuteStateTransition))
      .map((sp) => sp.stateId)
  }

  const getEditableStates = (): string[] => {
    // Los administradores pueden editar en todos los estados
    if (isAdmin()) {
      return [] // Retorna array vacío indicando "sin restricciones"
    }

    if (!auth?.statePermissions) return []

    return auth.statePermissions
      .filter((sp) => sp.permissions.includes(StateTransitionPermission.EditStateTransition))
      .map((sp) => sp.stateId)
  }

  // Verificar permisos específicos de negociaciones
  const hasNegotiationPermission = (permission: NegotiationsPermission): boolean => {
    // Los administradores tienen todos los permisos
    if (isAdmin()) {
      return true
    }

    if (!auth?.permissions) return false
    return auth.permissions.includes(permission as any)
  }

  const canActivateNegotiation = (): boolean => {
    return hasNegotiationPermission(NegotiationsPermission.ActivateNegotiation)
  }

  const canRescindApprovedNegotiation = (): boolean => {
    return hasNegotiationPermission(NegotiationsPermission.RescindApprovedNegotiation)
  }

  return {
    isAdmin,
    hasStatePermission,
    canReadState,
    canExecuteState,
    canEditState,
    getReadableStates,
    getExecutableStates,
    getEditableStates,
    hasNegotiationPermission,
    canActivateNegotiation,
    canRescindApprovedNegotiation
  }
}
